import { parseISO, differenceInMinutes, format, startOfDay, subDays, subMonths, subYears } from 'date-fns';
/**
 * Récupère toutes les sessions de l'utilisateur depuis localStorage
 */
export function getAllUserSessions() {
const sessions = localStorage.getItem('userSessions');
const currentSession = localStorage.getItem('currentUserSession');

let allSessions = sessions ? JSON.parse(sessions) : [];

// Ajouter la session courante si elle existe
if (currentSession) {
const current = JSON.parse(currentSession);
allSessions.push(current);
}

return allSessions;
}

/**
 * Récupère tous les événements utilisateur depuis localStorage
 */
export function getAllUserEvents() {
const events = localStorage.getItem('userEvents');
return events ? JSON.parse(events) : [];
}

/**
 * Récupère tous les projets depuis le store Redux ou localStorage
 */
export function getAllUserProjects() {
const projects = localStorage.getItem('userProjects');
return projects ? JSON.parse(projects) : [];
}

/**
 * Calcule les statistiques des sessions utilisateur
 * @returns {Object} Statistiques détaillées des sessions
 */
export function calculateSessionStats(selectedPeriod = '7 derniers jours') {
  const allSessions = getAllUserSessions().filter(session => 
    session.startedAt && session.endedAt
  );
  
  const filteredSessions = filterSessionsByPeriod(allSessions, selectedPeriod);
  
  if (filteredSessions.length === 0) {
    return {
      averageDuration: 0,
      totalSessions: 0,
      totalTime: 0,
      shortestSession: 0,
      longestSession: 0,
      sessionLengths: [],
      averageActivityTime: 0,
      averageInactivityTime: 0,
      totalActivityTime: 0,
      totalInactivityTime: 0
    };
  }

  const sessionLengths = filteredSessions.map(session => {
    const start = parseISO(session.startedAt);
    const end = parseISO(session.endedAt);
    const duration = differenceInMinutes(end, start);
    return Math.round(duration); // CORRECTION: Arrondir chaque durée
  });

  // CORRECTION: Arrondir les temps d'activité/inactivité
  const activityTimes = filteredSessions.map(session => 
    Math.round(session.activity_duration || 0)
  );
  const inactivityTimes = filteredSessions.map(session => 
    Math.round(session.inactivity_duration || 0)
  );
  
  const totalTime = sessionLengths.reduce((sum, length) => sum + length, 0);
  const totalActivityTime = activityTimes.reduce((sum, time) => sum + time, 0);
  const totalInactivityTime = inactivityTimes.reduce((sum, time) => sum + time, 0);
  
  // CORRECTION: Arrondir tous les résultats finaux
  return {
    averageDuration: Math.round(totalTime / filteredSessions.length),
    totalSessions: filteredSessions.length,
    totalTime: Math.round(totalTime),
    shortestSession: Math.round(Math.min(...sessionLengths)),
    longestSession: Math.round(Math.max(...sessionLengths)),
    sessionLengths,
    averageActivityTime: Math.round(totalActivityTime / filteredSessions.length),
    averageInactivityTime: Math.round(totalInactivityTime / filteredSessions.length),
    totalActivityTime: Math.round(totalActivityTime),
    totalInactivityTime: Math.round(totalInactivityTime),
    activityTimes,
    inactivityTimes
  };
}

function calculateActivityFromHeartbeats(session) {
  if (!session.heartbeats || session.heartbeats.length === 0) return 0;
  
  let activityTime = 0;
  const HEARTBEAT_EXPECTED_INTERVAL = 1;
  const HEARTBEAT_TOLERANCE = 0.5;
  
  for (let i = 0; i < session.heartbeats.length - 1; i++) {
    const currentHeartbeat = new Date(session.heartbeats[i]).getTime();
    const nextHeartbeat = new Date(session.heartbeats[i + 1]).getTime();
    const intervalMinutes = (nextHeartbeat - currentHeartbeat) / (60 * 1000); // CORRECTION: Pas de Math.floor
    
    if (intervalMinutes <= HEARTBEAT_EXPECTED_INTERVAL + HEARTBEAT_TOLERANCE) {
      activityTime += intervalMinutes;
    } else {
      activityTime += HEARTBEAT_EXPECTED_INTERVAL;
    }
  }
  
  if (session.heartbeats.length > 0) {
    const lastHeartbeat = new Date(session.heartbeats[session.heartbeats.length - 1]).getTime();
    const endTime = session.endedAt ? new Date(session.endedAt).getTime() : Date.now();
    const timeSinceLastHeartbeat = (endTime - lastHeartbeat) / (60 * 1000); // CORRECTION: Pas de Math.floor
    
    if (timeSinceLastHeartbeat <= HEARTBEAT_EXPECTED_INTERVAL + HEARTBEAT_TOLERANCE) {
      activityTime += timeSinceLastHeartbeat;
    } else {
      activityTime += HEARTBEAT_EXPECTED_INTERVAL;
    }
  }
  
  return Math.round(activityTime); // CORRECTION: Arrondir le résultat final
}

function calculateInactivityFromHeartbeats(session) {
  if (!session.heartbeats || session.heartbeats.length === 0) return 0;
  
  let inactivityTime = 0;
  const HEARTBEAT_EXPECTED_INTERVAL = 1; // CORRECTION: Utiliser 1 au lieu de 5
  const HEARTBEAT_TOLERANCE = 0.5; // CORRECTION: Utiliser 0.5 au lieu de 2
  
  for (let i = 0; i < session.heartbeats.length - 1; i++) {
    const currentHeartbeat = new Date(session.heartbeats[i]).getTime();
    const nextHeartbeat = new Date(session.heartbeats[i + 1]).getTime();
    const intervalMinutes = (nextHeartbeat - currentHeartbeat) / (60 * 1000); // CORRECTION: Pas de Math.floor
    
    if (intervalMinutes > HEARTBEAT_EXPECTED_INTERVAL + HEARTBEAT_TOLERANCE) {
      inactivityTime += (intervalMinutes - HEARTBEAT_EXPECTED_INTERVAL);
    }
  }
  
  if (session.heartbeats.length > 0) {
    const lastHeartbeat = new Date(session.heartbeats[session.heartbeats.length - 1]).getTime();
    const endTime = session.endedAt ? new Date(session.endedAt).getTime() : Date.now();
    const timeSinceLastHeartbeat = (endTime - lastHeartbeat) / (60 * 1000); // CORRECTION: Pas de Math.floor
    
    if (timeSinceLastHeartbeat > HEARTBEAT_EXPECTED_INTERVAL + HEARTBEAT_TOLERANCE) {
      inactivityTime += (timeSinceLastHeartbeat - HEARTBEAT_EXPECTED_INTERVAL);
    }
  }
  
  return Math.round(inactivityTime); // CORRECTION: Arrondir le résultat final
}

function filterSessionsByPeriod(sessions, period) {
  const now = new Date();
  let startDate;

  switch (period) {
    case 'Aujourd\'hui':
      startDate = startOfDay(now);
      break;
    case '7 derniers jours':
      startDate = subDays(now, 7);
      break;
    case '30 derniers jours':
      startDate = subDays(now, 30);
      break;
    case '3 derniers mois':
      startDate = subMonths(now, 3);
      break;
    case '1 dernière année':
      startDate = subYears(now, 1);
      break;
    default:
      startDate = subDays(now, 7);
  }

  return sessions.filter(session => {
    const sessionDate = parseISO(session.startedAt);
    return sessionDate >= startDate && sessionDate <= now;
  });
}
// return {
// averageDuration, // en minutes
// totalSessions: sessions.length,
// totalTime, // en minutes
// shortestSession: Math.min(...sessionLengths),
// longestSession: Math.max(...sessionLengths),
// averageActivityTime, // en minutes
// sessionLengths,
// // Données pour graphiques
// sessionsByDay: groupSessionsByDay(sessions),
// sessionsByHour: groupSessionsByHour(sessions)
// };
// }

function groupSessionsByDay(sessions) {
const grouped = {};
sessions.forEach(session => {
const day = format(parseISO(session.startedAt), 'yyyy-MM-dd');
if (!grouped[day]) grouped[day] = [];
grouped[day].push(session);
});
return grouped;
}

function groupSessionsByHour(sessions) {
const grouped = {};
sessions.forEach(session => {
const hour = format(parseISO(session.startedAt), 'HH');
if (!grouped[hour]) grouped[hour] = 0;
grouped[hour]++;
});
return grouped;
}

export function calculateToolUsagePercentage(space, period = '7 derniers jours') {
  const userEvents = JSON.parse(localStorage.getItem('userEvents')) || [];

  // Filter events by the selected period using the `datetime` field
  const filteredEvents = userEvents.filter(event => {
    if (!event.datetime) return false; // Skip events without a datetime

    try {
      const eventDate = parseISO(event.datetime);
      const now = new Date();

      switch (period) {
        case 'Aujourd\'hui':
          return differenceInMinutes(now, eventDate) <= 1440;
        case '7 derniers jours':
          return differenceInMinutes(now, eventDate) <= 10080;
        case '30 derniers jours':
          return differenceInMinutes(now, eventDate) <= 43200;
        default:
          return true;
      }
    } catch (error) {
      console.error('Invalid datetime format:', event.datetime);
      return false; // Skip events with invalid datetimes
    }
  });

  // Extract only valid 'tool' properties from tool objects using the `timestamp` field
  const tools = filteredEvents.flatMap(event => 
    (event[`${space}Tools`] || []).filter(toolObj => {
      if (!toolObj.timestamp) return false; // Skip tools without a timestamp

      try {
        const toolDate = parseISO(toolObj.timestamp);
        const now = new Date();

        switch (period) {
          case 'Aujourd\'hui':
            return differenceInMinutes(now, toolDate) <= 1440;
          case '7 derniers jours':
            return differenceInMinutes(now, toolDate) <= 10080;
          case '30 derniers jours':
            return differenceInMinutes(now, toolDate) <= 43200;
          default:
            return true;
        }
      } catch (error) {
        console.error('Invalid timestamp format:', toolObj.timestamp);
        return false; // Skip tools with invalid timestamps
      }
    }).map(toolObj => toolObj.tool).filter(tool => tool && typeof tool === 'string')
  );

  const totalUsage = tools.length;

  if (totalUsage === 0) return [];

  const toolCounts = tools.reduce((acc, tool) => {
    acc[tool] = (acc[tool] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(toolCounts).map(([tool, count]) => ({
    tool,
    percentage: ((count / totalUsage) * 100).toFixed(2),
  }));
}